import React from 'react';
import { Field, useFormikContext } from 'formik';
import { Col, Form, Row } from 'react-bootstrap';
import { endOfWeek, format, parseISO, startOfWeek } from 'date-fns';
import { he } from 'date-fns/locale/he';
import {
  formatWorkDateDisplay,
  sumColumns,
  sumDayHours,
} from './dailyHoursUtils';
import { generateWorkerTimesheetFrontendId } from '../../clients/worker_timesheet_client';

/** Hebrew labels for hour columns (mobile and desktop). */
const HOUR_FIELD_LABELS = {
  regular_hours: 'שעות בסיס',
  daycare_hours: 'צהרון',
  meeting_hours: 'ישיבה',
  training_hours: 'השתלמות',
  variable_hours: 'שעות משתנות',
  transport_hours: 'שעות הובלה',
};

/** Daily hour input keys. Variable sits after training and before transport. */
const HOUR_FIELD_KEYS = [
  'regular_hours',
  'daycare_hours',
  'meeting_hours',
  'training_hours',
  'variable_hours',
  'transport_hours',
];

/** Footer keys matching `sumColumns`. */
const HOUR_TOTAL_KEYS = {
  regular_hours: 'regular',
  daycare_hours: 'daycare',
  meeting_hours: 'meeting',
  training_hours: 'training',
  variable_hours: 'variable',
  transport_hours: 'transport',
};

/**
 * List layout: one row per eligible work day (Sun–Thu) with six hour inputs, plus a monthly
 * totals row below the scroll area (matches former grid `tfoot`).
 * Date stays `md={2}`; the six hour columns share the rest of the row (`col-md`)
 * because six `md={2}` fields plus the date would overflow 12 columns.
 * Marks `_rowTouched` when any hour field changes.
 *
 * @component
 * @returns {JSX.Element}
 */
const DailyHoursListView = () => {
  const { values, setFieldValue } = useFormikContext();
  const rows = values.daily_hours || [];
  const totals = sumColumns(rows);

  /**
   * Marks the daily row as user-edited and propagates the field update.
   *
   * @param {number} idx
   * @param {string} fieldKey
   * @param {import('react').ChangeEvent<HTMLInputElement>} e
   */
  const handleHourChange = (idx, fieldKey, e) => {
    setFieldValue(`daily_hours.${idx}._rowTouched`, true);
    setFieldValue(`daily_hours.${idx}.${fieldKey}`, e.target.value);
  };

  /**
   * Clears sick-note related fields on a daily row.
   *
   * @param {number} idx
   */
  const clearSickNoteFields = idx => {
    setFieldValue(`daily_hours.${idx}.sick_note_frontend_id`, '');
    setFieldValue(`daily_hours.${idx}.sick_note_file`, null);
    setFieldValue(`daily_hours.${idx}.sick_note_filename`, '');
    setFieldValue(`daily_hours.${idx}.sick_note_content_type`, '');
    setFieldValue(`daily_hours.${idx}.sick_note_size`, 0);
  };

  /**
   * Toggles sick-day status and enforces mutual exclusion with off-day.
   *
   * @param {number} idx
   * @param {boolean} checked
   */
  const handleSickToggle = (idx, checked) => {
    setFieldValue(`daily_hours.${idx}._rowTouched`, true);
    setFieldValue(`daily_hours.${idx}.is_sick_day`, checked);
    if (checked) {
      setFieldValue(`daily_hours.${idx}.is_off_day`, false);
      return;
    }
    clearSickNoteFields(idx);
  };

  /**
   * Toggles off-day status and enforces mutual exclusion with sick-day.
   *
   * @param {number} idx
   * @param {boolean} checked
   */
  const handleOffToggle = (idx, checked) => {
    setFieldValue(`daily_hours.${idx}._rowTouched`, true);
    setFieldValue(`daily_hours.${idx}.is_off_day`, checked);
    if (checked) {
      setFieldValue(`daily_hours.${idx}.is_sick_day`, false);
      clearSickNoteFields(idx);
    }
  };

  /**
   * Stores selected sick-note file and metadata for payload upload flow.
   *
   * @param {number} idx
   * @param {File|null} file
   */
  const handleSickNoteChange = (idx, file) => {
    setFieldValue(`daily_hours.${idx}._rowTouched`, true);
    if (!file) {
      clearSickNoteFields(idx);
      return;
    }
    const fid =
      rows[idx]?.sick_note_frontend_id || generateWorkerTimesheetFrontendId();
    setFieldValue(`daily_hours.${idx}.sick_note_frontend_id`, fid);
    setFieldValue(`daily_hours.${idx}.sick_note_file`, file);
    setFieldValue(`daily_hours.${idx}.sick_note_filename`, file.name || '');
    setFieldValue(
      `daily_hours.${idx}.sick_note_content_type`,
      file.type || 'application/octet-stream',
    );
    setFieldValue(`daily_hours.${idx}.sick_note_size`, file.size || 0);
  };

  /**
   * Calendar week bounds (Sunday–Saturday) label for headings; empty if parsing fails.
   *
   * @param {string} workDateIso
   * @returns {string}
   */
  const weekRangeLabel = workDateIso => {
    try {
      const d = parseISO(workDateIso);
      const start = startOfWeek(d, { weekStartsOn: 0 });
      const end = endOfWeek(d, { weekStartsOn: 0 });
      return `${format(start, 'dd/MM/yyyy')} – ${format(end, 'dd/MM/yyyy')}`;
    } catch {
      return '';
    }
  };

  /**
   * @param {string} workDateIso
   * @returns {number|null} startOfWeek timestamp for comparison
   */
  const weekStartTime = workDateIso => {
    try {
      return startOfWeek(parseISO(workDateIso), { weekStartsOn: 0 }).getTime();
    } catch {
      return null;
    }
  };

  return (
    <div className="worker-ts-daily-list border rounded p-2 bg-white">
      <Row className="fw-semibold small text-muted mb-2 d-none d-md-flex">
        <Col md={2}>תאריך</Col>
        {HOUR_FIELD_KEYS.map(fieldKey => (
          <Col md key={fieldKey} className="lh-sm">
            {HOUR_FIELD_LABELS[fieldKey]}
          </Col>
        ))}
      </Row>
      <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        {rows.map((row, idx) => {
          let weekdayLabel = '';
          try {
            weekdayLabel = format(parseISO(row.work_date), 'EEE', {
              locale: he,
            });
          } catch {
            weekdayLabel = '';
          }
          const prevWeekStart =
            idx > 0 ? weekStartTime(rows[idx - 1].work_date) : undefined;
          const thisWeekStart = weekStartTime(row.work_date);
          const showWeekHeader =
            idx === 0 || thisWeekStart !== prevWeekStart;
          const dayTotal = sumDayHours(row);
          const rangeText = weekRangeLabel(row.work_date);
          return (
            <React.Fragment key={row.work_date}>
              {showWeekHeader && rangeText ? (
                <>
                  <div className="worker-ts-week-banner small fw-semibold px-1">
                    {'שבוע: '}
                    {rangeText}
                  </div>
                  <hr className="worker-ts-week-divider my-2" />
                </>
              ) : null}
              <Row className="worker-ts-day-row worker-ts-day-sep align-items-center py-1 gx-1">
                <Col md={2} xs={12} className="small mb-1 mb-md-0">
                  <span className="text-muted me-2">
                    {weekdayLabel}
                    {weekdayLabel ? ' ' : null}
                  </span>
                  <span className="fw-medium">
                    {formatWorkDateDisplay(row.work_date)}
                  </span>
                </Col>
                {HOUR_FIELD_KEYS.map(fieldKey => (
                  <Col md xs={6} key={fieldKey} className="mb-1">
                    <Form.Label className="d-md-none small mb-0 text-muted">
                      {HOUR_FIELD_LABELS[fieldKey] || fieldKey}
                    </Form.Label>
                    <Field name={`daily_hours.${idx}.${fieldKey}`}>
                      {({ field }) => (
                        <Form.Control
                          {...field}
                          type="number"
                          min={0}
                          step={0.25}
                          inputMode="decimal"
                          className="worker-ts-hour-input"
                          onChange={e => handleHourChange(idx, fieldKey, e)}
                        />
                      )}
                    </Field>
                  </Col>
                ))}
                <Col md={12} xs={12} className="small text-muted mt-1">
                  <span className="d-md-none">{'סה״כ יום: '}</span>
                  <span className="d-none d-md-inline">{'סך יומי: '}</span>
                  {dayTotal.toFixed(2)}
                </Col>
                <Col xs={12} className="worker-ts-day-status-wrap pt-1">
                  <div className="d-flex flex-wrap gap-3 align-items-center">
                    <Form.Check
                      type="checkbox"
                      id={`daily_hours.${idx}.is_sick_day`}
                      label="יום מחלה"
                      checked={Boolean(row.is_sick_day)}
                      onChange={e => handleSickToggle(idx, e.target.checked)}
                    />
                    <Form.Check
                      type="checkbox"
                      id={`daily_hours.${idx}.is_off_day`}
                      label="יום חופשה/חופש"
                      checked={Boolean(row.is_off_day)}
                      onChange={e => handleOffToggle(idx, e.target.checked)}
                    />
                  </div>
                  {row.is_sick_day ? (
                    <div className="worker-ts-sick-file mt-2">
                      <Form.Label className="small mb-1">
                        אישור רופא/מרפאה
                      </Form.Label>
                      <Form.Control
                        type="file"
                        onChange={e =>
                          handleSickNoteChange(idx, e.target.files?.[0] || null)
                        }
                      />
                      {row.sick_note_file instanceof File ? (
                        <div className="small text-muted mt-1">
                          נבחר: {row.sick_note_file.name}
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </Col>
              </Row>
            </React.Fragment>
          );
        })}
      </div>

      <Row className="d-none d-md-flex fw-semibold border-top mt-2 pt-2 mx-0 bg-light rounded-bottom align-items-center gx-1">
        <Col md={2} className="small">
          סה״כ חודשי
        </Col>
        {HOUR_FIELD_KEYS.map(fieldKey => (
          <Col md key={fieldKey} className="text-end small">
            {totals[HOUR_TOTAL_KEYS[fieldKey]].toFixed(2)}
          </Col>
        ))}
        <Col md={12} className="text-end small mt-1">
          סך כולל: {totals.total.toFixed(2)}
        </Col>
      </Row>

      <Row className="d-md-none fw-semibold border-top mt-2 pt-2 mx-0 bg-light rounded-bottom gy-1 small">
        <Col xs={12} className="mb-1">
          סה״כ חודשי
        </Col>
        {HOUR_FIELD_KEYS.map(fieldKey => (
          <React.Fragment key={fieldKey}>
            <Col xs={6} className="text-muted">
              {HOUR_FIELD_LABELS[fieldKey]}
            </Col>
            <Col xs={6} className="text-end">
              {totals[HOUR_TOTAL_KEYS[fieldKey]].toFixed(2)}
            </Col>
          </React.Fragment>
        ))}
        <Col xs={6} className="text-muted pt-1 border-top mt-1">
          סך כולל
        </Col>
        <Col xs={6} className="text-end pt-1 border-top mt-1">
          {totals.total.toFixed(2)}
        </Col>
      </Row>
    </div>
  );
};

export default DailyHoursListView;
